/**
 * Turns the API's onboarding progress into the four checklist steps the user
 * sees: wording, status and where each step's button goes.
 *
 * The API decides what is done; this only decides how to say it. Wording
 * changes with the workspace, so a newcomer to an established workspace is
 * told to find their team rather than create the first one.
 */

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

function teamStep({ team_count: teamCount }, base) {
  if (teamCount === 0) {
    return {
      title: 'Create your first team',
      description:
        'A team is the group you want to assess, like a product squad or a department.',
      cta: { label: 'Create a team', to: `${base}/teams/add` },
    };
  }

  return {
    title: 'Find your team',
    description: `Your workspace has ${plural(
      teamCount,
      'team'
    )}. Favorite the one you work with, or create a new one.`,
    cta: { label: 'Browse teams', to: `${base}/teams` },
  };
}

function assessmentStep({ favorite_team: favoriteTeam }, base) {
  return {
    title: 'Start an assessment',
    description: favoriteTeam
      ? `Choose a template, review its questions and launch it for ${favoriteTeam.name}.`
      : 'Choose a template, review its questions and launch it for one of your teams.',
    cta: {
      label: 'Choose a template',
      to: favoriteTeam
        ? `${base}/teams/${favoriteTeam.id}/assessments/new`
        : `${base}/teams`,
    },
  };
}

function shareLink(assessment, base) {
  if (assessment.type === 'live') {
    return `/assessments/${assessment.id}/host`;
  }

  return `${base}/teams/${assessment.team_id}/assessments/${assessment.id}/launch`;
}

function inviteStep({ own_assessment: ownAssessment }, base) {
  return {
    title: 'Invite your team to respond',
    description: ownAssessment
      ? 'Share the link or passphrase so your team can answer. This checks off once someone responds.'
      : 'Once your assessment is running, share its link or passphrase with your team.',
    cta: ownAssessment
      ? { label: 'Share your assessment', to: shareLink(ownAssessment, base) }
      : null,
  };
}

function resultsStep({ latest_results: latestResults, steps }, base) {
  if (!latestResults) {
    return {
      title: 'Review your results',
      description:
        "Results show up here as soon as people respond. You'll see where the team is strong and where it can grow.",
      cta: null,
    };
  }

  const ownResponses = steps.some(
    (step) => step.key === 'invite' && step.status === 'done'
  );

  return {
    title: 'Review results',
    description: ownResponses
      ? `Responses are in for ${latestResults.team_name}. See where the team is strong and where it can grow.`
      : `Results are ready for ${latestResults.team_name}. Take a look while you wait for yours.`,
    cta: {
      label: 'View results',
      to: `${base}/teams/${latestResults.team_id}/assessments/${latestResults.key}`,
    },
  };
}

const BUILDERS = {
  team: teamStep,
  assessment: assessmentStep,
  invite: inviteStep,
  results: resultsStep,
};

export function buildSteps(progress, workspaceId) {
  if (!progress) {
    return [];
  }

  const base = `/w/${workspaceId}`;

  return progress.steps.map((step, index) => ({
    ...step,
    number: index + 1,
    isNext: step.key === progress.next_step,
    ...BUILDERS[step.key](progress, base),
  }));
}

export function countFinished(progress) {
  return (progress?.steps ?? []).filter((step) => step.status !== 'todo')
    .length;
}

/**
 * The extras listed beside the checklist. None of them are tracked.
 */
export function buildSuggestions(progress, workspaceId) {
  if (!progress) {
    return [];
  }

  const base = `/w/${workspaceId}`;
  const suggestions = [];

  if (progress.sample_team) {
    suggestions.push({
      key: 'sample',
      title: 'Explore the sample team',
      description: 'See a few rounds of results before your own come in.',
      to: `${base}/teams/${progress.sample_team.id}`,
    });
  } else if (!progress.sample_created) {
    suggestions.push({
      key: 'sample',
      title: 'Explore a sample team',
      description: 'See a few rounds of results before your own come in.',
      createsSample: true,
    });
  }

  suggestions.push(
    {
      key: 'invite-colleague',
      title: 'Invite a colleague',
      description: 'Share the work of running assessments.',
      to: `${base}/users/invite`,
    },
    {
      key: 'rubrics',
      title: 'Explore rubrics',
      description: "Track your team's growth against a shared standard.",
      to: `${base}/rubrics`,
    }
  );

  return suggestions;
}
